import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";

const initialForm = {
  first_name: "",
  middle_name: "",
  last_name: "",
  gender: "",
  date_of_birth: "",
  phone: "",
  email: "",
  address: "",
  current_class: "",
  stream: "",
  current_class_group: "",
  enrollment_date: "",
  previous_school: "",
  guardian_name: "",
  guardian_relationship: "",
  guardian_phone: "",
  guardian_email: "",
  guardian_address: "",
  medical_conditions: "",
  allergies: "",
  emergency_contact_name: "",
  emergency_contact_phone: "",
  temporary_password: "",
};

const steps = [
  { key: "student", label: "Student Details", icon: "bi-person" },
  { key: "guardian", label: "Guardian Details", icon: "bi-people" },
  { key: "health", label: "Health & Emergency", icon: "bi-heart-pulse" },
  { key: "login", label: "Login Details", icon: "bi-key" },
];

function StudentRegisterModal({ show, onClose, onCreated }) {
  const [formData, setFormData] = useState(initialForm);
  const [classGroups, setClassGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const loadClassGroups = async () => {
      if (!show) return;

      try {
        const response = await axiosInstance.get("academic/class-groups/");
        setClassGroups(response.data || []);
      } catch (error) {
        console.error(error);
        toast.error("Could not load academic classes.");
      }
    };

    loadClassGroups();
  }, [show]);

  const selectedClassGroup = useMemo(
    () => classGroups.find((group) => String(group.id) === String(formData.current_class_group)),
    [classGroups, formData.current_class_group]
  );

  if (!show) return null;

  const isLastStep = activeStep === steps.length - 1;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClassGroupChange = (event) => {
    const groupId = event.target.value;
    const group = classGroups.find((item) => String(item.id) === String(groupId));

    setFormData((prev) => ({
      ...prev,
      current_class_group: groupId,
      current_class: group?.class_name || group?.display_name || "",
      stream: group?.stream_name || "",
    }));
  };

  const resetForm = () => {
    setFormData(initialForm);
    setActiveStep(0);
  };

  const validateStep = (stepIndex = activeStep) => {
    if (stepIndex === 0) {
      if (!formData.first_name.trim() || !formData.last_name.trim()) {
        toast.warning("First name and last name are required.");
        return false;
      }

      if (classGroups.length > 0 && !formData.current_class_group) {
        toast.warning("Please select the student's class group.");
        return false;
      }
    }

    if (stepIndex === 1) {
      if (!formData.guardian_name.trim() || !formData.guardian_phone.trim()) {
        toast.warning("Guardian name and phone are required.");
        return false;
      }
    }

    return true;
  };

  const goToStep = (stepIndex) => {
    if (stepIndex > activeStep && !validateStep(activeStep)) return;
    setActiveStep(stepIndex);
  };

  const goNext = () => {
    if (!validateStep(activeStep)) return;
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const goBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateStep(0) || !validateStep(1)) return;

    try {
      setSaving(true);

      const payload = Object.fromEntries(
        Object.entries(formData).filter(([, value]) => value !== "")
      );

      const response = await axiosInstance.post("students/register/", payload);
      toast.success("Student registered successfully.");
      onCreated(response.data?.student);
      resetForm();
    } catch (error) {
      console.error(error);
      const data = error.response?.data;
      const message =
        data?.detail ||
        Object.values(data || {})?.flat?.()?.[0] ||
        "Failed to register student.";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const renderStudentStep = () => (
    <div className="row g-3">
      <div className="col-md-4">
        <label className="form-label">First Name</label>
        <input
          name="first_name"
          className="form-control"
          value={formData.first_name}
          onChange={handleChange}
          required
        />
      </div>
      <div className="col-md-4">
        <label className="form-label">Middle Name</label>
        <input
          name="middle_name"
          className="form-control"
          value={formData.middle_name}
          onChange={handleChange}
        />
      </div>
      <div className="col-md-4">
        <label className="form-label">Last Name</label>
        <input
          name="last_name"
          className="form-control"
          value={formData.last_name}
          onChange={handleChange}
          required
        />
      </div>

      <div className="col-md-3">
        <label className="form-label">Gender</label>
        <select
          name="gender"
          className="form-select"
          value={formData.gender}
          onChange={handleChange}
        >
          <option value="">Select</option>
          <option value="MALE">Male</option>
          <option value="FEMALE">Female</option>
          <option value="OTHER">Other</option>
        </select>
      </div>
      <div className="col-md-3">
        <label className="form-label">Date of Birth</label>
        <input
          type="date"
          name="date_of_birth"
          className="form-control"
          value={formData.date_of_birth}
          onChange={handleChange}
        />
      </div>
      <div className="col-md-6">
        <label className="form-label">Class Group</label>
        <select
          className="form-select"
          value={formData.current_class_group}
          onChange={handleClassGroupChange}
          required={classGroups.length > 0}
        >
          <option value="">
            {classGroups.length ? "Select class group" : "No class groups configured"}
          </option>
          {classGroups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.academic_year_name} - {group.display_name}
              {group.class_teacher_name ? ` (${group.class_teacher_name})` : ""}
            </option>
          ))}
        </select>
      </div>
      <div className="col-md-3">
        <label className="form-label">Class</label>
        <input className="form-control" value={formData.current_class} readOnly />
      </div>
      <div className="col-md-3">
        <label className="form-label">Stream</label>
        <input className="form-control" value={formData.stream} readOnly />
      </div>

      <div className="col-md-6">
        <label className="form-label">Academic Year</label>
        <input
          className="form-control"
          value={selectedClassGroup?.academic_year_name || ""}
          readOnly
        />
      </div>
      <div className="col-md-6">
        <label className="form-label">Class Teacher</label>
        <input
          className="form-control"
          value={selectedClassGroup?.class_teacher_name || "Not assigned"}
          readOnly
        />
      </div>

      <div className="col-md-4">
        <label className="form-label">Enrollment Date</label>
        <input
          type="date"
          name="enrollment_date"
          className="form-control"
          value={formData.enrollment_date}
          onChange={handleChange}
        />
      </div>
      <div className="col-md-4">
        <label className="form-label">Student Phone Optional</label>
        <input
          name="phone"
          className="form-control"
          value={formData.phone}
          onChange={handleChange}
        />
      </div>
      <div className="col-md-4">
        <label className="form-label">Student Email Optional</label>
        <input
          type="email"
          name="email"
          className="form-control"
          value={formData.email}
          onChange={handleChange}
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">Previous School</label>
        <input
          name="previous_school"
          className="form-control"
          value={formData.previous_school}
          onChange={handleChange}
        />
      </div>
      <div className="col-md-6">
        <label className="form-label">Student Address</label>
        <input
          name="address"
          className="form-control"
          value={formData.address}
          onChange={handleChange}
        />
      </div>
    </div>
  );

  const renderGuardianStep = () => (
    <div className="row g-3">
      <div className="col-12">
        <div className="alert alert-light border mb-0">
          Guardian details will later be connected to Parent/Guardian accounts and student linking.
        </div>
      </div>

      <div className="col-md-4">
        <label className="form-label">Guardian Name</label>
        <input
          name="guardian_name"
          className="form-control"
          value={formData.guardian_name}
          onChange={handleChange}
          required
        />
      </div>
      <div className="col-md-4">
        <label className="form-label">Relationship</label>
        <select
          name="guardian_relationship"
          className="form-select"
          value={formData.guardian_relationship}
          onChange={handleChange}
        >
          <option value="">Select</option>
          <option value="FATHER">Father</option>
          <option value="MOTHER">Mother</option>
          <option value="GUARDIAN">Guardian</option>
          <option value="SPONSOR">Sponsor</option>
          <option value="OTHER">Other</option>
        </select>
      </div>
      <div className="col-md-4">
        <label className="form-label">Guardian Phone</label>
        <input
          name="guardian_phone"
          className="form-control"
          value={formData.guardian_phone}
          onChange={handleChange}
          required
        />
      </div>
      <div className="col-md-6">
        <label className="form-label">Guardian Email Optional</label>
        <input
          type="email"
          name="guardian_email"
          className="form-control"
          value={formData.guardian_email}
          onChange={handleChange}
        />
      </div>
      <div className="col-md-6">
        <label className="form-label">Guardian Address Optional</label>
        <input
          name="guardian_address"
          className="form-control"
          value={formData.guardian_address}
          onChange={handleChange}
        />
      </div>
    </div>
  );

  const renderHealthStep = () => (
    <div className="row g-3">
      <div className="col-md-6">
        <label className="form-label">Emergency Contact Name</label>
        <input
          name="emergency_contact_name"
          className="form-control"
          placeholder="Defaults to guardian name"
          value={formData.emergency_contact_name}
          onChange={handleChange}
        />
      </div>
      <div className="col-md-6">
        <label className="form-label">Emergency Contact Phone</label>
        <input
          name="emergency_contact_phone"
          className="form-control"
          placeholder="Defaults to guardian phone"
          value={formData.emergency_contact_phone}
          onChange={handleChange}
        />
      </div>
      <div className="col-md-6">
        <label className="form-label">Medical Conditions</label>
        <textarea
          name="medical_conditions"
          rows="4"
          className="form-control"
          value={formData.medical_conditions}
          onChange={handleChange}
        ></textarea>
      </div>
      <div className="col-md-6">
        <label className="form-label">Allergies</label>
        <textarea
          name="allergies"
          rows="4"
          className="form-control"
          value={formData.allergies}
          onChange={handleChange}
        ></textarea>
      </div>
    </div>
  );

  const renderLoginStep = () => (
    <div className="row g-3">
      <div className="col-12">
        <div className="alert alert-success border-0">
          <div className="fw-bold">Student login setup</div>
          <div>
            The system will generate a student number like{" "}
            <strong>TGCS0000001BT</strong>. The student will login using that number
            and the temporary password.
          </div>
        </div>
      </div>

      <div className="col-md-6">
        <label className="form-label">Temporary Password Optional</label>
        <input
          name="temporary_password"
          className="form-control"
          placeholder="Leave blank to auto-generate"
          value={formData.temporary_password}
          onChange={handleChange}
        />
      </div>
      <div className="col-md-6">
        <label className="form-label">Password Policy</label>
        <input
          className="form-control"
          value="Student must change password after first login"
          disabled
        />
      </div>
    </div>
  );

  const renderStepContent = () => {
    if (activeStep === 0) return renderStudentStep();
    if (activeStep === 1) return renderGuardianStep();
    if (activeStep === 2) return renderHealthStep();
    return renderLoginStep();
  };

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ background: "rgba(0,0,0,0.45)" }}
    >
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content border-0 shadow">
          <div className="modal-header">
            <div>
              <h5 className="modal-title fw-bold">Register Student</h5>
              <small className="text-muted">
                Student number will be generated automatically, e.g. TGCS0000001BT.
              </small>
            </div>
            <button type="button" className="btn-close" onClick={handleClose}></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="px-3 pt-3">
              <div className="row g-2">
                {steps.map((step, index) => {
                  const isActive = activeStep === index;
                  const isComplete = activeStep > index;

                  return (
                    <div className="col-md-3" key={step.key}>
                      <button
                        type="button"
                        className={`btn w-100 text-start d-flex align-items-center gap-2 border ${
                          isActive ? "text-white" : "btn-light"
                        }`}
                        style={
                          isActive
                            ? { background: "#125b2d", borderColor: "#125b2d" }
                            : {}
                        }
                        onClick={() => goToStep(index)}
                      >
                        <span
                          className={`rounded-circle d-flex align-items-center justify-content-center ${
                            isActive ? "bg-white text-success" : "bg-success text-white"
                          }`}
                          style={{ width: 28, height: 28, flexShrink: 0 }}
                        >
                          {isComplete ? (
                            <i className="bi bi-check"></i>
                          ) : (
                            index + 1
                          )}
                        </span>
                        <span>
                          <span className="d-block fw-semibold">{step.label}</span>
                          <small className={isActive ? "text-white-50" : "text-muted"}>
                            <i className={`bi ${step.icon} me-1`}></i>
                            Step {index + 1}
                          </small>
                        </span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div
              className="modal-body"
              style={{ maxHeight: "62vh", overflowY: "auto" }}
            >
              {renderStepContent()}
            </div>

            <div className="modal-footer d-flex justify-content-between">
              <div>
                <button type="button" className="btn btn-light" onClick={handleClose}>
                  Cancel
                </button>
              </div>

              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-success"
                  onClick={goBack}
                  disabled={activeStep === 0 || saving}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Back
                </button>

                {!isLastStep ? (
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={goNext}
                    disabled={saving}
                  >
                    Next
                    <i className="bi bi-arrow-right ms-2"></i>
                  </button>
                ) : (
                  <button type="submit" className="btn btn-success" disabled={saving}>
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-person-plus me-2"></i>
                        Register Student
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default StudentRegisterModal;
